import axios from 'axios';
import * as Sentry from '@sentry/react';
import { v1 as uuidv1 } from 'uuid';
import dataProvider from '../providers/dataProvider';

// Dedup uploads by File reference. If the same File is passed in twice
// (e.g. drop-time upload followed by an accidental save-time upload),
// the second call returns the in-flight or completed promise instead
// of starting another Mux upload.
const uploadCache = new WeakMap();

/**
 * Create video file in rest-api database using mux upload id
 * @param {*} fileName
 * @param {*} muxUploadId Mux upload Id
 * @param {*} file
 * @returns FileVideo
 */
const createFileInDb = (fileName, muxUploadId, file) => {
  return dataProvider('CREATE', 'file/video', {
    data: {
      fileName,
      muxUploadId,
      originalName: file.name,
      size: file.size,
    },
  });
};

/**
 * Upload file by uploadUrl
 * @param {*} uploadUrl Mux upload url
 * @param {*} file
 * @param {*} progressFunc
 * @returns void
 */
const uploadFile = (uploadUrl, file, progressFunc) => {
  const options = {
    headers: { 'Content-Type': file.type },
    onUploadProgress: progressFunc,
  };

  return axios.put(uploadUrl, file, options);
};

const doUpload = (video, progressFunc) => {
  const file = video.rawFile;
  const ext = file.name.split('.').pop();
  const fileName = uuidv1();

  let uploadId = null;
  let uploadUrl = null;
  let videoId = null;

  return dataProvider('CREATE_MUX_UPLOAD', 'mux/upload', {})
    .then((res) => {
      uploadId = res.data.id;
      uploadUrl = res.data.url;
    })
    .then(() => createFileInDb(`${fileName}.${ext}`, uploadId, file))
    .then((res) => {
      videoId = res.data.id;
      return uploadFile(uploadUrl, file, progressFunc);
    })
    .then(() => ({ id: videoId }))
    .catch((err) => {
      // Drop the cache entry so a subsequent retry can run.
      uploadCache.delete(file);
      Sentry.captureException('Error while video uploading', err);
      return false;
    });
};

const uploadVideoService = (video, progressFunc = null) => {
  if (!video || !video.rawFile) {
    return Promise.resolve(null);
  }
  const file = video.rawFile;
  const cached = uploadCache.get(file);
  if (cached) {
    return cached;
  }
  const promise = doUpload(video, progressFunc);
  uploadCache.set(file, promise);
  return promise;
};

export default uploadVideoService;
